import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { cancelSooCoolOrders } from "../../../../functions/api/soocool";
import { CARRIER_LABELS, CARRIER_OPTIMOROUTE, CARRIER_SOOCOOL, CARRIER_TRUNKRS } from "../../../../functions/carrierBooking";
import { planCarrierRelease, releaseUpdates } from "../../../../functions/carrierRelease";
import { getPaymentStatus } from "../../../../functions/formatters";
import { useAuth, useSupplements } from "../../../../data/hooks";

/**
 * "Vervoerder loskoppelen" on the orders page (client request 2026-10-19): one or more orders taken
 * away from their carrier and put back to paid, so Nog te doen offers them again. The rule - what
 * happens per carrier and which status comes back - lives in functions/carrierRelease.js.
 *
 * First it shows what will happen per order, then it runs and shows what did happen. SooCool is
 * cancelled through the api (the same endpoint as the SooCool page); Trunkrs and OptimoRoute are only
 * released in the CMS, and the result says which shipments staff still have to remove there.
 *
 * While it runs the modal cannot be closed: its mask is what keeps staff from clicking on.
 */

const orderKey = (order) => `${order?.shopSource}|${order?.id}`;

const CarrierReleaseModal = ({ orders, onClose, updateOrdersFields }) => {
  const { t } = useTranslation();
  const { email: adminEmail, uid: adminUid } = useAuth();
  const { addHistoryEntry } = useSupplements();
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const open = Array.isArray(orders) && orders.length > 0;
  const plan = useMemo(() => planCarrierRelease(orders || []), [orders]);
  const toRelease = [...plan.viaSooCool, ...plan.manual];

  // A new set of orders starts on the preview again
  useEffect(() => {
    setOutcome(null);
  }, [orders]);

  const close = () => {
    if (running) return;
    onClose({ changed: (outcome?.released?.length || 0) > 0 });
  };

  const whatHappens = (item) => {
    if (item.carrier === CARRIER_SOOCOOL) return t("carrier_release.action_soocool");
    if (item.carrier === CARRIER_TRUNKRS) {
      return item.reference
        ? t("carrier_release.action_trunkrs_number", { number: item.reference })
        : t("carrier_release.action_trunkrs");
    }
    return t("carrier_release.action_optimoroute");
  };

  const run = async () => {
    setRunning(true);
    const released = [];
    const failed = [];
    const statusNotSaved = [];

    try {
      // 1. SooCool, one call per shop: each shop has its own api and SooCool account.
      const sooCoolReleased = [];
      const byShop = _.groupBy(plan.viaSooCool, (item) => item.order.shopSource);
      for (const [shopSource, items] of Object.entries(byShop)) {
        let results;
        try {
          results = await cancelSooCoolOrders(
            shopSource,
            items.map((item) => item.reference),
          );
        } catch (err) {
          items.forEach((item) => failed.push({ item, error: err?.message || String(err) }));
          continue;
        }
        items.forEach((item) => {
          const result = results.find((answer) => String(answer.sooCoolOrderId) === String(item.reference));
          if (!result) failed.push({ item, error: t("carrier_release.no_answer") });
          else if (result.status !== "cancelled") failed.push({ item, error: result.error || t("carrier_release.refused") });
          // Cancelled at SooCool, but the api left the order alone (it holds a newer shipment, say)
          else if (result.releasedDocumentId !== item.order.id) {
            failed.push({ item, error: result.note || t("carrier_release.not_released") });
          } else sooCoolReleased.push(item);
        });
      }

      // 2. Our side, in one batch per shop: the status for SooCool (the api already took the block
      // off), marker and status for Trunkrs and OptimoRoute.
      const entries = [...sooCoolReleased, ...plan.manual].map((item) => ({
        item,
        updates: releaseUpdates(item.order, item.carrier),
      }));
      const toWrite = entries.filter((entry) => Object.keys(entry.updates).length > 0);
      const { failed: writeFailed } =
        toWrite.length > 0
          ? await updateOrdersFields(toWrite.map(({ item, updates }) => ({ order: item.order, updates })))
          : { failed: [] };
      const writeErrors = new Map(writeFailed.map((entry) => [orderKey(entry.order), entry.error]));

      entries.forEach(({ item }) => {
        const error = writeErrors.get(orderKey(item.order));
        if (error === undefined) released.push(item);
        // SooCool no longer has it and the order is free to export again; only the status stayed.
        else if (item.carrier === CARRIER_SOOCOOL) statusNotSaved.push({ item, error });
        // Nothing changed in the CMS: staff must not remove it at Trunkrs or OptimoRoute either.
        else failed.push({ item, error });
      });

      // 3. The audit trail. Best effort: the release itself is done.
      await Promise.all(
        released.map(async (item) => {
          try {
            await addHistoryEntry(item.order, {
              adminEmail,
              adminUid,
              action: "carrier_released",
              details: {
                carrier: item.carrier,
                reference: item.reference ?? null,
                statusFrom: item.order.orderStatus || null,
                statusTo: item.statusAfter || null,
              },
            });
          } catch (err) {
            console.error("[CarrierReleaseModal] history entry failed", err);
          }
        }),
      );
    } finally {
      setOutcome({ released, failed, statusNotSaved });
      setRunning(false);
    }
  };

  const previewColumns = [
    { title: t("carrier_release.order_number"), render: (_, item) => item.order.orderId },
    {
      title: t("order.carrier"),
      render: (_, item) => <Tag>{CARRIER_LABELS[item.carrier]}</Tag>,
    },
    { title: t("carrier_release.what_happens"), render: (_, item) => whatHappens(item) },
    { title: t("carrier_release.status_after"), render: (_, item) => getPaymentStatus(item.statusAfter) || "-" },
  ];

  const orderList = (items) => items.map(({ order }) => order.orderId).join(", ");
  const errorList = (items) =>
    items.map(({ item, error }) => (
      <div key={orderKey(item.order)}>
        <strong>{item.order.orderId}</strong>: {error}
      </div>
    ));
  const manualFor = (carrier) => (outcome?.released || []).filter((item) => item.carrier === carrier);

  const renderPreview = () => (
    <>
      <p>{t("carrier_release.intro")}</p>
      {toRelease.length > 0 && (
        <Table
          size="small"
          rowKey={(item) => orderKey(item.order)}
          columns={previewColumns}
          dataSource={toRelease}
          pagination={false}
          style={{ marginBottom: 16 }}
        />
      )}
      {plan.manual.length > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t("carrier_release.manual_hint")} />
      )}
      {plan.skipped.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t("carrier_release.skipped_title", { count: plan.skipped.length })}
          description={plan.skipped
            .map(({ order, reason }) => `${order.orderId} (${t(`carrier_release.skip_${reason}`)})`)
            .join(", ")}
        />
      )}
    </>
  );

  const renderOutcome = () => {
    const trunkrs = manualFor(CARRIER_TRUNKRS);
    const optimoRoute = manualFor(CARRIER_OPTIMOROUTE);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {outcome.released.length > 0 && (
          <Alert
            type="success"
            showIcon
            message={t("carrier_release.released_title", { count: outcome.released.length })}
            description={orderList(outcome.released.map((item) => ({ order: item.order })))}
          />
        )}
        {trunkrs.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("carrier_release.remove_in_trunkrs_title", { count: trunkrs.length })}
            description={trunkrs
              .map((item) => (item.reference ? `${item.order.orderId} (${item.reference})` : item.order.orderId))
              .join(", ")}
          />
        )}
        {optimoRoute.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("carrier_release.remove_in_optimoroute_title", { count: optimoRoute.length })}
            description={orderList(optimoRoute.map((item) => ({ order: item.order })))}
          />
        )}
        {outcome.statusNotSaved.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("carrier_release.status_not_saved_title", { count: outcome.statusNotSaved.length })}
            description={errorList(outcome.statusNotSaved)}
          />
        )}
        {outcome.failed.length > 0 && (
          <Alert
            type="error"
            showIcon
            message={t("carrier_release.failed_title", { count: outcome.failed.length })}
            description={errorList(outcome.failed)}
          />
        )}
      </div>
    );
  };

  const footer = outcome
    ? [
        <Button key="close" type="primary" onClick={close}>
          {t("carrier_release.close")}
        </Button>,
      ]
    : [
        <Button key="back" onClick={close} disabled={running}>
          {t("carrier_release.back")}
        </Button>,
        <Button
          key="release"
          type="primary"
          danger
          loading={running}
          disabled={toRelease.length === 0}
          onClick={run}
        >
          {t("carrier_release.confirm", { count: toRelease.length })}
        </Button>,
      ];

  return (
    <Modal
      open={open}
      title={t("carrier_release.title")}
      width={760}
      onCancel={close}
      closable={!running}
      maskClosable={false}
      keyboard={!running}
      footer={footer}
      destroyOnClose
    >
      {outcome ? renderOutcome() : renderPreview()}
    </Modal>
  );
};

export default CarrierReleaseModal;
